import type { ImgHTMLAttributes } from "react";

import markUrl from "./badminton-warriors-mark.webp?url";
import smallMarkUrl from "./badminton-warriors-mark-128.webp?url";

/** Boxes up to this size use the 128px copy (sharp at 2x density). */
const SMALL_MARK_MAX_PX = 64;

/**
 * Named display sizes. `bar` fits the 48px staff desktop bar, `phoneBar` the
 * 52px phone bar, `appBar` the player's 64px app bar.
 */
export const BRAND_MARK_SIZES = {
  bar: 28,
  phoneBar: 32,
  appBar: 40,
} as const;

export type BrandMarkSize = keyof typeof BRAND_MARK_SIZES;

export interface BrandMarkProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> {
  /** A named bar size, or an explicit square box (px number or CSS length). */
  size?: BrandMarkSize | number | string;
}

/**
 * The Badminton Warriors product mark: the one component every app (Player,
 * Business, Platform) renders its logo from. The artwork is one generated pair
 * beside this file (see README.md): the 768px master and its 128px copy for
 * the bars. Each app's `public/favicon.png` comes from the same artwork.
 *
 * Decorative by default (`alt=""`); pass `alt` when no nearby label names the
 * brand. The image keeps its aspect ratio inside the square box.
 */
export function BrandMark({ alt = "", size = 64, style, ...props }: BrandMarkProps) {
  const box =
    typeof size === "string" && size in BRAND_MARK_SIZES
      ? BRAND_MARK_SIZES[size as BrandMarkSize]
      : size;
  const small = typeof box === "number" && box <= SMALL_MARK_MAX_PX;
  return (
    <img
      {...props}
      alt={alt}
      src={small ? smallMarkUrl : markUrl}
      style={{
        display: "block",
        flex: "0 0 auto",
        width: box,
        height: box,
        objectFit: "contain",
        ...style,
      }}
    />
  );
}
